import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { useEdiPasswordResetComplete } from '@/api/hanly-v2/edi/edi';
import type { StandardizedError } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Button, LinkButton } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { PasswordInput } from '@/components/ui/password-input';
import { passwordConfirmSchema, passwordSchema } from '@/utils/schemas';

const passwordResetCompleteSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordConfirmSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  });

type PasswordResetCompleteFormData = z.infer<typeof passwordResetCompleteSchema>;

const InvalidLink = ({ message }: { message: string }) => (
  <div className="flex flex-col gap-5">
    <p role="alert" className="flex gap-2 font-bold text-red-700">
      <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
      {message}
    </p>
    <LinkButton to="/password/reset" size="lg" fullWidth className="text-base">
      再設定メールを再送信する
    </LinkButton>
  </div>
);

/** パスワード再設定メールのリンク(?key=)から新しいパスワードを設定する */
export const PasswordResetCompletePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetKey = searchParams.get('key');
  const [keyError, setKeyError] = useState('');
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<PasswordResetCompleteFormData>({
    resolver: zodResolver(passwordResetCompleteSchema),
  });

  const { mutate: completeReset, isPending } = useEdiPasswordResetComplete();

  const onSubmit = (data: PasswordResetCompleteFormData) => {
    if (!resetKey) return;
    setApiError('');
    completeReset(
      { data: { key: resetKey, new_password: data.password } },
      {
        onSuccess: () => {
          toast.success('パスワードを変更しました。新しいパスワードでログインしてください。');
          navigate('/signin', { replace: true });
        },
        onError: async (error) => {
          if (error.status === 429) {
            setApiError('短時間に送信が繰り返されました。しばらく時間をおいてから再度お試しください。');
            return;
          }
          try {
            const body = (await error.json()) as StandardizedError;
            if (body.errors.some((e) => e.attr === 'key')) {
              setKeyError(
                'リンクが無効か、有効期限（1時間）が切れています。お手数ですが、再設定メールをもう一度送信してください。',
              );
              return;
            }
            const passwordError = body.errors.find((e) => e.attr === 'new_password');
            if (passwordError) {
              setError('password', { message: passwordError.detail });
              return;
            }
          } catch {
            // 本文が読めない場合は共通の文言にする
          }
          setApiError('パスワードの変更に失敗しました。時間をおいて再度お試しください。');
        },
      },
    );
  };

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-14 md:max-w-md md:px-0">
      <div className="flex flex-col gap-1">
        <h2>新しいパスワードの設定</h2>
        {resetKey && !keyError && <p className="text-neutral-600">新しいパスワードを入力してください。</p>}
      </div>

      {!resetKey || keyError ? (
        <InvalidLink
          message={keyError || 'リンクが正しくありません。メールのリンクから再度アクセスしてください。'}
        />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
          {apiError && (
            <p role="alert" className="flex gap-2 font-bold text-red-700">
              <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
              {apiError}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="font-bold">
              新しいパスワード
            </label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              invalid={!!errors.password}
              className="h-12 text-base md:h-11"
              {...register('password')}
            />
            <FormError message={errors.password?.message} />
            <p className="text-sm text-neutral-600">9文字以上で、数字だけのパスワードは使用できません。</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="confirmPassword" className="font-bold">
              新しいパスワード（確認）
            </label>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              invalid={!!errors.confirmPassword}
              className="h-12 text-base md:h-11"
              {...register('confirmPassword')}
            />
            <FormError message={errors.confirmPassword?.message} />
          </div>
          <Button type="submit" size="lg" fullWidth loading={isPending} className="mt-1 text-base">
            パスワードを変更
          </Button>
        </form>
      )}
    </div>
  );
};
